import { useState, useEffect } from 'react'
import { apiFetch } from '@/lib/apiFetch'

interface EmailPref {
  id: number
  parentId: number
  childId: number
  emailType: string
  enabled: boolean
  dayOfWeek: number
  hourOfDay: number
  timezone: string | null
}

interface Child {
  id: number
  displayName: string
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

export default function EmailPreferencesSection({ children }: { children: Child[] }) {
  const [prefs, setPrefs] = useState<EmailPref[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadPrefs()
  }, [children])

  const loadPrefs = async () => {
    try {
      const allPrefs: EmailPref[] = []
      for (const child of children) {
        const childPrefs = await apiFetch(`/email-preferences?childId=${child.id}`)
        allPrefs.push(...childPrefs)
      }
      setPrefs(allPrefs)
    } catch {
      // Silently fail — preferences may not exist yet
    } finally {
      setLoading(false)
    }
  }

  const updatePref = async (pref: EmailPref, updates: Partial<EmailPref>) => {
    try {
      await apiFetch(`/email-preferences/${pref.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          enabled: updates.enabled ?? pref.enabled,
          dayOfWeek: updates.dayOfWeek ?? pref.dayOfWeek,
          hourOfDay: updates.hourOfDay ?? pref.hourOfDay,
          timezone: updates.timezone ?? pref.timezone,
        }),
      })
      setPrefs(prev => prev.map(p => p.id === pref.id ? { ...p, ...updates } : p))
    } catch {
      // silent
    }
  }

  if (loading) return null
  if (children.length === 0) return null

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Email Notifications</h3>

      {children.map(child => {
        const childPrefs = prefs.filter(p => p.childId === child.id)
        const goalMetPref = childPrefs.find(p => p.emailType === 'GOAL_MET')
        const summaryPref = childPrefs.find(p => p.emailType === 'WEEKLY_SUMMARY')

        return (
          <div key={child.id} className="p-4 rounded-lg border border-border space-y-3">
            <p className="font-medium">{child.displayName}</p>

            {goalMetPref && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={goalMetPref.enabled}
                  onChange={(e) => updatePref(goalMetPref, { enabled: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                />
                <div>
                  <span className="text-sm font-medium">Goal met alerts</span>
                  <p className="text-xs text-muted-foreground">Get emailed when they hit their daily goal</p>
                </div>
              </label>
            )}

            {summaryPref && (
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={summaryPref.enabled}
                    onChange={(e) => updatePref(summaryPref, { enabled: e.target.checked })}
                    className="w-4 h-4 accent-primary"
                  />
                  <div>
                    <span className="text-sm font-medium">Weekly summary</span>
                    <p className="text-xs text-muted-foreground">Weekly progress report with stats comparison</p>
                  </div>
                </label>

                {summaryPref.enabled && (
                  <div className="flex gap-3 ml-7">
                    <select
                      value={summaryPref.dayOfWeek}
                      onChange={(e) => updatePref(summaryPref, { dayOfWeek: Number(e.target.value) })}
                      className="text-sm px-2 py-1 border border-border rounded bg-background"
                    >
                      {DAYS.map((d, i) => (
                        <option key={i} value={i}>{d}</option>
                      ))}
                    </select>
                    <select
                      value={summaryPref.hourOfDay}
                      onChange={(e) => updatePref(summaryPref, { hourOfDay: Number(e.target.value) })}
                      className="text-sm px-2 py-1 border border-border rounded bg-background"
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>
                          {h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {childPrefs.length === 0 && (
              <p className="text-sm text-muted-foreground">No email preferences set up yet.</p>
            )}
          </div>
        )
      })}
    </div>
  )
}
